import React, { useEffect, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';

interface CustomPortfolioProps {
  id: number;
  content: any;
  preview: boolean;
  onChange: (id: number, content: any) => void;
}

/** Custom Portfolio: only portfolio xlsx file selector. All widgets from the selected portfolio are shown in the fact sheet. */
export const CustomPortfolio: React.FC<CustomPortfolioProps> = ({ id, content, preview, onChange }) => {
  const [isinsInput, setIsinsInput] = useState<string>('');

  useEffect(() => {
    const fromArray = Array.isArray(content?.isins) ? content.isins.join(', ') : '';
    setIsinsInput(fromArray);
  }, [content?.isins]);

  const parseIsins = (raw: string): string[] =>
    raw
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);

  const handleIsinsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    setIsinsInput(raw);
    onChange(id, {
      ...content,
      // New mode: identify portfolio by ISIN set, not by upload selector id.
      isins: parseIsins(raw),
      portfolioUploadId: '',
    });
  };

  if (preview) {
    const previewIsins = parseIsins(isinsInput);
    return (
      <Box sx={{ p: 2, border: '1px dashed #ccc', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Custom Portfolio Widget
        </Typography>
        {previewIsins.length > 0 && (
          <Typography variant="caption" display="block" mt={1}>
            Linked to ISIN group: {previewIsins.join(', ')} — all widgets from this portfolio will be shown.
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Custom Portfolio Configuration
      </Typography>

      <TextField
        fullWidth
        multiline
        minRows={2}
        label="Customer Portfolio ISINs"
        placeholder="D11, D22, D33"
        value={isinsInput}
        onChange={handleIsinsChange}
        helperText="Enter ISINs separated by commas. Example: D11, D22, D33"
        sx={{ mb: 2 }}
      />

      {parseIsins(isinsInput).length > 0 && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Selected ISIN group: <strong>{parseIsins(isinsInput).join(', ')}</strong>
          </Typography>
        </Box>
      )}
    </Box>
  );
};
